import {
  Typography,
  Box,
  Container,
  CssBaseline,
  List,
  ListItem,
  ListItemText,
  Paper,
} from '@mui/material';

import { TenantType } from '../../components/types';
import CountryFlag from '../../components/common/CountryFlag';
import Image from '../../components/common/Image';
import { flagCodes } from '../../util/options';

type ClientPropertiesTabProps = {
  tenant: TenantType;
};

type RowProps = {
  key: string;
  label: string;
  postfix: string;
  value: any;
};

const Row = (props: RowProps) => {
  const { key, label, postfix, value } = props;

  return (
    <ListItem alignItems="flex-start" key={key}>
      <Paper sx={{ p: 1, pl: 3, width: '100%' }} elevation={3}>
        <ListItemText
          primary={
            <>
              <Typography sx={{ display: 'inline', fontWeight: 'medium' }}>
                {label}:{postfix}
              </Typography>
              <Typography
                sx={{ display: 'inline' }}
                component="span"
                variant="body1"
                color="text.primary"
              >
                {value}
              </Typography>
            </>
          }
        />
      </Paper>
    </ListItem>
  );
};
const ClientPropertiesTab = (props: ClientPropertiesTabProps) => {
  const { tenant } = props;
  return (
    <Container>
      <CssBaseline />
      <Box
        sx={{
          marginTop: 2,
          marginBottom: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <List
          sx={{ width: '100%', maxWidth: 800, bgcolor: 'background.paper' }}
        >
          <ListItem alignItems="flex-start" key={'tenantCode'}>
            <Paper sx={{ p: 1, pl: 3, width: '100%' }} elevation={3}>
              <ListItemText
                primary={
                  <>
                    <Typography
                      sx={{ display: 'inline', fontWeight: 'medium' }}
                    >
                      Tenant Code:{' '}
                    </Typography>
                    <Typography
                      sx={{ display: 'inline' }}
                      component="span"
                      variant="body1"
                      color="text.primary"
                    >
                      {tenant.code}
                    </Typography>
                  </>
                }
              />
            </Paper>
          </ListItem>
          <ListItem alignItems="flex-start" key={'tenantName'}>
            <Paper sx={{ p: 1, pl: 3, width: '100%' }} elevation={3}>
              <ListItemText
                primary={
                  <>
                    <Typography
                      sx={{ display: 'inline', fontWeight: 'medium' }}
                    >
                      Tenant Name:{' '}
                    </Typography>
                    <Typography
                      sx={{ display: 'inline' }}
                      component="span"
                      variant="body1"
                      color="text.primary"
                    >
                      {tenant.name}
                    </Typography>
                  </>
                }
              />
            </Paper>
          </ListItem>
          <ListItem alignItems="flex-start" key={'currency'}>
            <Paper sx={{ p: 1, pl: 3, width: '100%' }} elevation={3}>
              <ListItemText
                primary={
                  <>
                    <Typography
                      sx={{ display: 'inline', fontWeight: 'medium' }}
                    >
                      Source Currency:{' '}
                    </Typography>
                    <CountryFlag
                      countryCode={flagCodes[tenant.currency]}
                      otherProps={{
                        style: {
                          fontSize: '3em',
                          lineHeight: '3em',
                        },
                      }}
                      currency={tenant.currency}
                    />
                  </>
                }
              />
            </Paper>
          </ListItem>
          <ListItem alignItems="flex-start" key={'maxBonus'}>
            <Paper sx={{ p: 1, pl: 3, width: '100%' }} elevation={3}>
              <ListItemText
                primary={
                  <>
                    <Typography
                      sx={{ display: 'inline', fontWeight: 'medium' }}
                    >
                      Max Bonus:{' '}
                    </Typography>
                    <Typography
                      sx={{ display: 'inline' }}
                      component="span"
                      variant="body1"
                      color="text.primary"
                    >
                      {tenant.maxBonus}
                    </Typography>
                  </>
                }
              />
            </Paper>
          </ListItem>
          <ListItem alignItems="flex-start" key={'commission'}>
            <Paper sx={{ p: 1, pl: 3, width: '100%' }} elevation={3}>
              <ListItemText
                primary={
                  <>
                    <Typography
                      sx={{ display: 'inline', fontWeight: 'medium' }}
                    >
                      Commission %:{' '}
                    </Typography>
                    <Typography
                      sx={{ display: 'inline' }}
                      component="span"
                      variant="body1"
                      color="text.primary"
                    >
                      {tenant.commission}
                    </Typography>
                  </>
                }
              />
            </Paper>
          </ListItem>
          <ListItem alignItems="flex-start" key={'duplicateThreshold'}>
            <Paper sx={{ p: 1, pl: 3, width: '100%' }} elevation={3}>
              <ListItemText
                primary={
                  <>
                    <Typography
                      sx={{ display: 'inline', fontWeight: 'medium' }}
                    >
                      Duplicate Threshold (Hrs):{' '}
                    </Typography>
                    <Typography
                      sx={{ display: 'inline' }}
                      component="span"
                      variant="body1"
                      color="text.primary"
                    >
                      {tenant.duplicateThreshold}
                    </Typography>
                  </>
                }
              />
            </Paper>
          </ListItem>
          <Row
            key="rateChangeThreshold1"
            label="Admin Rate Change Threshold"
            postfix="%"
            value={tenant.rateChangeThreshold1}
          />
          <Row
            key="rateChangeThreshold2"
            label="Agent Rate Change Threshold"
            postfix="%"
            value={tenant.rateChangeThreshold2}
          />
          <Row
            key="rateChangeThreshold3"
            label="Satellite Rate Change Threshold"
            postfix="%"
            value={tenant.rateChangeThreshold3}
          />
          <Row
            key="receiptLabel"
            label="Receipt Label"
            postfix=" "
            value={tenant.receiptLabel}
          />
          <ListItem alignItems="flex-start" key={'receiptLabel'}>
            <Paper sx={{ p: 1, pl: 3, width: '100%' }} elevation={3}>
              <ListItemText
                primary={
                  <>
                    <Typography
                      sx={{ display: 'inline', fontWeight: 'medium' }}
                    >
                      Receipt Label:{' '}
                    </Typography>
                    <Typography
                      sx={{ display: 'inline' }}
                      component="span"
                      variant="body1"
                      color="text.primary"
                    >
                      {tenant.receiptLabel}
                    </Typography>
                  </>
                }
              />
            </Paper>
          </ListItem>
          <ListItem alignItems="flex-start" key={'receiptLineOne'}>
            <Paper sx={{ p: 1, pl: 3, width: '100%' }} elevation={3}>
              <ListItemText
                primary={
                  <>
                    <Typography
                      sx={{ display: 'inline', fontWeight: 'medium' }}
                    >
                      Receipt Line 1:{' '}
                    </Typography>
                    <Typography
                      sx={{ display: 'inline' }}
                      component="span"
                      variant="body1"
                      color="text.primary"
                    >
                      {tenant.receiptLineOne}
                    </Typography>
                  </>
                }
              />
            </Paper>
          </ListItem>
          <ListItem alignItems="flex-start" key={'receiptLineTwo'}>
            <Paper sx={{ p: 1, pl: 3, width: '100%' }} elevation={3}>
              <ListItemText
                primary={
                  <>
                    <Typography
                      sx={{ display: 'inline', fontWeight: 'medium' }}
                    >
                      Receipt Line 2:{' '}
                    </Typography>
                    <Typography
                      sx={{ display: 'inline' }}
                      component="span"
                      variant="body1"
                      color="text.primary"
                    >
                      {tenant.receiptLineTwo}
                    </Typography>
                  </>
                }
              />
            </Paper>
          </ListItem>
          <ListItem alignItems="flex-start" key={'receiptFootnote'}>
            <Paper sx={{ p: 1, pl: 3, width: '100%' }} elevation={3}>
              <ListItemText
                primary={
                  <>
                    <Typography
                      sx={{ display: 'inline', fontWeight: 'medium' }}
                    >
                      Receipt Footnote:{' '}
                    </Typography>
                    <Typography
                      sx={{ display: 'inline' }}
                      component="span"
                      variant="body1"
                      color="text.primary"
                    >
                      {tenant.receiptFootnote}
                    </Typography>
                  </>
                }
              />
            </Paper>
          </ListItem>
          <ListItem alignItems="flex-start" key={'logo'}>
            <Paper sx={{ p: 1, pl: 3, width: '100%' }} elevation={3}>
              <ListItemText
                primary={
                  <>
                    <Typography
                      sx={{ display: 'inline', fontWeight: 'medium' }}
                    >
                      Logo:{' '}
                    </Typography>
                    <Image src={tenant.logo || ''} />
                  </>
                }
              />
            </Paper>
          </ListItem>
        </List>
      </Box>
    </Container>
  );
};

export default ClientPropertiesTab;
